/**
 * backgroundService — CRUD de fondos virtuales administrados.
 *
 * Los fondos se almacenan en:
 *   Firestore: config/teleconsulta/backgrounds/{docId}
 *   Storage:   backgrounds/{filename}
 *
 * El médico puede ver los fondos activos y guardar su preferencia en:
 *   medicos/{uid}/preferences/teleconsulta → { backgroundId }
 */

import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
  serverTimestamp,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../config/firebase';

/* ── Types ─────────────────────────────────────────────────────────────── */

export interface AdminBackground {
  id: string;
  name: string;
  storageRef: string;
  downloadUrl: string;
  order: number;
  active: boolean;
  createdAt?: unknown;
}

export interface DoctorBgPreference {
  backgroundId: string | null;
}

/* ── Constants ─────────────────────────────────────────────────────────── */

const BACKGROUNDS_COLLECTION = 'config/teleconsulta/backgrounds';
const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/* ── Admin CRUD ────────────────────────────────────────────────────────── */

export async function getAdminBackgrounds(): Promise<AdminBackground[]> {
  const q = query(
    collection(db, BACKGROUNDS_COLLECTION),
    orderBy('order', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as AdminBackground));
}

export async function uploadBackground(
  file: File,
  name: string,
  order: number,
): Promise<AdminBackground> {
  // Validations
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Formato no soportado. Use JPEG, PNG o WebP.');
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('El archivo excede 3MB.');
  }

  // Upload to Storage
  const ext = file.name.split('.').pop() ?? 'jpg';
  const filename = `${Date.now()}_${name.replace(/\s+/g, '_').toLowerCase()}.${ext}`;
  const storagePath = `backgrounds/${filename}`;
  const storageRefObj = ref(storage, storagePath);

  await uploadBytes(storageRefObj, file, {
    contentType: file.type,
    cacheControl: 'public, max-age=604800',
  });

  const downloadUrl = await getDownloadURL(storageRefObj);

  // Save to Firestore
  const docRef = await addDoc(collection(db, BACKGROUNDS_COLLECTION), {
    name,
    storageRef: storagePath,
    downloadUrl,
    order,
    active: true,
    createdAt: serverTimestamp(),
  });

  return {
    id: docRef.id,
    name,
    storageRef: storagePath,
    downloadUrl,
    order,
    active: true,
  };
}

export async function updateBackground(
  id: string,
  data: Partial<Pick<AdminBackground, 'name' | 'order' | 'active'>>,
): Promise<void> {
  await updateDoc(doc(db, BACKGROUNDS_COLLECTION, id), data);
}

export async function deleteBackground(bg: AdminBackground): Promise<void> {
  // Delete from Storage
  try {
    await deleteObject(ref(storage, bg.storageRef));
  } catch {
    // Ignore if file already deleted
  }
  // Delete Firestore doc
  await deleteDoc(doc(db, BACKGROUNDS_COLLECTION, bg.id));
}

/* ── Doctor preference ─────────────────────────────────────────────────── */

export async function getDoctorBgPreference(uid: string): Promise<string | null> {
  const snap = await getDoc(doc(db, 'medicos', uid, 'preferences', 'teleconsulta'));
  if (!snap.exists()) return null;
  return (snap.data() as DoctorBgPreference).backgroundId ?? null;
}

export async function saveDoctorBgPreference(uid: string, backgroundId: string | null): Promise<void> {
  await setDoc(
    doc(db, 'medicos', uid, 'preferences', 'teleconsulta'),
    { backgroundId },
    { merge: true },
  );
}
